import { ACHIEVEMENTS } from "./achievements.js";
import { shopItemById, ShopItem } from "./shop-items.js";
import { Profile } from "./type.app.js";

export const LOCATION_WIDE = "/location/start-wide.png";
export const LOCATION_TALL = "/location/start-tall.png";
export const LOCATION_WIDE_ASPECT = 1672 / 941;
export const LOCATION_TALL_ASPECT = 887 / 1774;
export const START_LOCATION_ID = "start";

export type GameLocation = {
  id: string;
  name: string;
  wide: string;
  tall: string;
  /** Achievement family that unlocks this location. The starting meadow has none. */
  family?: string;
};

export const LOCATIONS: GameLocation[] = [
  {
    id: START_LOCATION_ID,
    name: "Meadow",
    wide: LOCATION_WIDE,
    tall: LOCATION_TALL,
  },
  {
    id: "mountain",
    name: "Mountain",
    wide: "/location/location-mountain-wide.png",
    tall: "/location/location-mountain-tall.png",
    family: "streak",
  },
  {
    id: "castle",
    name: "Castle",
    wide: "/location/location-castle-wide.png",
    tall: "/location/location-castle-tall.png",
    family: "shop",
  },
  {
    id: "island",
    name: "Island",
    wide: "/location/location-island-wide.png",
    tall: "/location/location-island-tall.png",
    family: "bank",
  },
  {
    id: "farm",
    name: "Farm",
    wide: "/location/location-farm-wide.png",
    tall: "/location/location-farm-tall.png",
    family: "earned",
  },
  {
    id: "desert",
    name: "Desert",
    wide: "/location/location-desert-wide.png",
    tall: "/location/location-desert-tall.png",
    family: "level",
  },
  {
    id: "dark-forest",
    name: "Dark Forest",
    wide: "/location/location-dark-forest-wide.png",
    tall: "/location/location-dark-forest-tall.png",
    family: "read",
  },
  {
    id: "cowboy-town",
    name: "Cowboy Town",
    wide: "/location/location-cowboy-town-wide.png",
    tall: "/location/location-cowboy-town-tall.png",
    family: "master",
  },
];

export const locationById = (id: string | undefined): GameLocation => {
  return LOCATIONS.find((item) => item.id === id) ?? LOCATIONS[0];
};

export const locationForFamily = (family: string): GameLocation | undefined => {
  return LOCATIONS.find((item) => item.family === family);
};

export const locationUnlockMessage = (location: GameLocation): string => {
  return `Achieve this trophy to unlock the ${location.name}`;
};

export const SHOP_STACK_DESKTOP = 6;
export const SHOP_STACK_MOBILE = 5;

export type ItemPoint = {
  x: number;
  y: number;
};

export const clampItemPoint = (point: ItemPoint): ItemPoint => ({
  x: Math.min(0.96, Math.max(0.04, point.x)),
  y: Math.min(0.96, Math.max(0.04, point.y)),
});

export const locationImageForViewport = (
  width: number,
  height: number,
  locationId: string = START_LOCATION_ID,
): string => {
  const location = locationById(locationId);
  const viewport = width / Math.max(1, height);
  const wideGap = Math.abs(viewport - LOCATION_WIDE_ASPECT);
  const tallGap = Math.abs(viewport - LOCATION_TALL_ASPECT);
  return wideGap <= tallGap ? location.wide : location.tall;
};

export const syncLocations = (profile: Profile): Profile => {
  const earned = new Set(profile.achievements);
  const unlocked = new Set(profile.unlockedLocations);
  unlocked.add(START_LOCATION_ID);
  const newly: string[] = [];
  for (const location of LOCATIONS) {
    if (!location.family || unlocked.has(location.id)) continue;
    const family = ACHIEVEMENTS.filter((item) => item.family === location.family);
    if (family.length > 0 && family.every((item) => earned.has(item.id))) {
      unlocked.add(location.id);
      newly.push(location.id);
    }
  }
  const unlockedLocations = LOCATIONS.map((item) => item.id).filter((id) => unlocked.has(id));
  const currentLocationId =
    newly[newly.length - 1] ??
    (unlocked.has(profile.currentLocationId) ? profile.currentLocationId : START_LOCATION_ID);
  if (
    currentLocationId === profile.currentLocationId &&
    unlockedLocations.length === profile.unlockedLocations.length &&
    unlockedLocations.every((id) => profile.unlockedLocations.includes(id))
  ) {
    return profile;
  }
  return { ...profile, unlockedLocations, currentLocationId };
};

export const ownedItemsNewestFirst = (inventory: string[]): ShopItem[] => {
  const seen = new Set<string>();
  const items: ShopItem[] = [];
  for (let index = inventory.length - 1; index >= 0; index -= 1) {
    const id = inventory[index];
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const item = shopItemById(id);
    if (item) items.push(item);
  }
  return items;
};

export const defaultItemPosition = (index: number, count: number): ItemPoint => {
  const n = Math.max(1, count);
  const span = Math.min(0.58, 0.065 * Math.max(0, n - 1));
  const start = 0.14;
  const x = n === 1 ? 0.2 : start + (span * index) / Math.max(1, n - 1);
  return { x, y: 0.84 };
};

export type PlacedItem = {
  id: string;
  x: number;
  y: number;
  z: number;
  placed: boolean;
};

export const itemLayout = (ids: string[], saved: Record<string, ItemPoint>): PlacedItem[] => {
  const unplaced = ids.filter((id) => !saved[id]);
  return ids.map((id, index) => {
    const savedPoint = saved[id];
    if (savedPoint) {
      return { id, ...clampItemPoint(savedPoint), z: ids.length - index, placed: true };
    }
    const slot = unplaced.indexOf(id);
    const pos = defaultItemPosition(slot, unplaced.length);
    return { id, ...pos, z: ids.length - index, placed: false };
  });
};
